import { useState } from "react";
import { TextField, MenuItem, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FieldInfo from "./FieldInfo";
import { FIELD_INFO } from "./fieldInfoContent";

/**
 * Complete labeled form for a single plan (pricing row). Replaces the old
 * card. Pure presentation over the plans[] state: every control writes back
 * with the SAME key the card used, via onChange(planId, { target:{name,value} })
 * — so validatePlans + the save payload are byte-identical. Each field carries
 * an inline (i) FieldInfo with the exact spec copy.
 */

// The listing editor renders inside a drawer at z-index 2000, so a Select's
// popover menu (MUI default z-index 1300) would open BEHIND the drawer and be
// unclickable. The z-index override must reach the underlying Select via
// SelectProps.MenuProps — passing MenuProps at the TextField top level is
// silently ignored (that was the "dropdowns don't work" bug).
const menuProps = {
  SelectProps: {
    MenuProps: { PaperProps: { sx: { zIndex: 4100 } }, sx: { zIndex: 4100 } },
  },
};

function LabeledInfo({ label, infoKey }) {
  return (
    <span style={styles.labelRow}>
      {label}
      <FieldInfo text={FIELD_INFO[infoKey]} />
    </span>
  );
}

/** Rough length in days — only ever used to compare two durations. */
const DURATION_DAYS = { day: 1, week: 7, month: 30, quarter: 91, year: 365 };

const spanDays = (n, unit) => {
  const u = String(unit || "").toLowerCase();
  if (u === "lifetime") return Infinity;
  const per = DURATION_DAYS[u];
  const count = Number(n);
  if (!per || !Number.isFinite(count) || count <= 0) return null;
  return per * count;
};

/**
 * True when the plan guarantees its price for LESS time than one billing
 * cycle. "No expiry" is never too short, and anything we cannot measure is
 * left alone rather than guessed at.
 */
function validityShorterThanInterval(plan) {
  const validity = spanDays(
    plan?.n_price_validity_interval,
    plan?.price_validity_interval,
  );
  const billing = spanDays(plan?.n_interval, plan?.interval);
  if (validity === null || billing === null) return false;
  return validity < billing;
}

export default function PlanFormFields({
  plan,
  onChange,
  disabled,
  template,
  planErrors = {},
  allowDelete,
  onDuplicate,
  onDelete,
  isOwner,
  children,
}) {
  const [hlInput, setHlInput] = useState("");
  const [hlEdit, setHlEdit] = useState(null);
  if (!plan) return null;

  const err = planErrors[plan.id] || {};
  const isContact = plan.price_type === "contact";
  const set = (name, value) =>
    onChange(plan.id, { target: { name, value } });

  const highlights = Array.isArray(plan.plan_highlight)
    ? plan.plan_highlight
    : [];
  const commitHighlight = () => {
    const v = hlInput.trim();
    if (!v) return;
    let next;
    if (hlEdit != null) {
      next = highlights.map((h, i) => (i === hlEdit ? v : h));
    } else {
      next = [...highlights, v];
    }
    set("plan_highlight", next);
    setHlInput("");
    setHlEdit(null);
  };
  const removeHighlight = (i) =>
    set(
      "plan_highlight",
      highlights.filter((_, idx) => idx !== i),
    );

  const tagsValue = Array.isArray(plan.tags)
    ? plan.tags.join(", ")
    : plan.tags ?? "";

  const lifetime = plan.price_validity_interval === "lifetime";
  // Price validity used to be locked once a plan had subscriptions
  // (`!plan.allow_delete`). That is no longer the rule: since phase 4 a change
  // here does NOT re-price anyone — a live subscription keeps the term it was
  // sold on until its own lock expires, and only the next quote uses the new
  // one. So it is editable whenever the form is.
  const validityDisabled = disabled;

  // The only real constraint: a price cannot be guaranteed for less time than
  // the customer is billed for. A yearly plan held for one month would expire
  // mid-cycle, which the lock has no way to express.
  const validityTooShort = validityShorterThanInterval(plan);

  // SOLD BY ANOTHER VENDOR (see admin get_marketplace_pricing). Their prices
  // are theirs, managed in their vendor portal; what this form edits is the
  // house's OWN price on the same plan, which starts empty.
  const heldBy = plan.held_by || null;
  const heldTiers = (heldBy?.tiers || [])
    .map((u) => {
      const end = Number(u.EndRange);
      const range = `${u.StartRange ?? 1}–${!end || end >= 999999 ? "∞" : end}`;
      const price = Number(u.Marketplace_price);
      return Number.isFinite(price) && price > 0
        ? `${range}: ${plan.currency === "USD" ? "$" : "₹"}${price.toLocaleString("en-IN")}`
        : null;
    })
    .filter(Boolean);

  return (
    <div style={styles.wrap}>
      {heldBy && (
        <div
          style={{
            margin: "0 0 14px",
            padding: "10px 12px",
            borderRadius: 8,
            border: "1px solid #b9dcf6",
            background: "#e8f4fd",
            color: "#1f2430",
            fontSize: 13,
            lineHeight: 1.45,
          }}
        >
          <b>Sold by {heldBy.vendor_name}</b>
          {heldTiers.length ? ` · ${heldTiers.join(" · ")}` : ""}
          <div style={{ marginTop: 4, color: "#495569" }}>
            Their prices are managed in their vendor portal and are not edited
            here. The description is the catalogue's — your edit applies to
            this plan whoever sells it. Everything else below is Easexpense's
            own price, which starts empty: add a price range to sell it as
            Easexpense as well. Buyers keep getting {heldBy.vendor_name} until
            you switch the seller on Set Default.
          </div>
        </div>
      )}
      <div style={styles.grid}>
        <TextField
          fullWidth
          size="small"
          name="name"
          label={<LabeledInfo label="Plan name" infoKey="plan.name" />}
          value={plan.name ?? ""}
          onChange={(e) => set("name", e.target.value)}
          // The shelf's name is shared with the vendor selling it; renaming
          // goes through the catalogue's rename, not this form.
          disabled={disabled || Boolean(heldBy)}
          error={!!err.name}
          sx={styles.full}
        />
        <TextField
          fullWidth
          size="small"
          name="description"
          label={
            <LabeledInfo label="Description" infoKey="plan.description" />
          }
          value={plan.description ?? ""}
          onChange={(e) => set("description", e.target.value)}
          disabled={disabled}
          error={!!err.description}
          multiline
          minRows={2}
          sx={styles.full}
        />

        <TextField
          select
          fullWidth
          size="small"
          label={<LabeledInfo label="Price type" infoKey="plan.price_type" />}
          value={plan.price_type === "contact" ? "contact" : "price"}
          onChange={(e) => set("price_type", e.target.value)}
          disabled={disabled}
          {...menuProps}
        >
          <MenuItem value="price">Price</MenuItem>
          <MenuItem value="contact">Contact us</MenuItem>
        </TextField>

        {!isContact && (
          <TextField
            select
            fullWidth
            size="small"
            label={<LabeledInfo label="Currency" infoKey="plan.currency" />}
            value={plan.currency ?? ""}
            onChange={(e) => set("currency", e.target.value)}
            disabled={disabled}
            {...menuProps}
          >
            <MenuItem value="INR">INR (₹)</MenuItem>
            <MenuItem value="USD">USD ($)</MenuItem>
          </TextField>
        )}

        <TextField
          select
          fullWidth
          size="small"
          label={<LabeledInfo label="Region" infoKey="plan.region" />}
          value={plan.region ?? ""}
          onChange={(e) => set("region", e.target.value)}
          disabled={disabled}
          error={!!err.region}
          {...menuProps}
        >
          <MenuItem value="">--select--</MenuItem>
          <MenuItem value="GL">Global</MenuItem>
          <MenuItem value="US">US</MenuItem>
          <MenuItem value="IN">India</MenuItem>
        </TextField>

        <TextField
          select
          fullWidth
          size="small"
          label={<LabeledInfo label="Status" infoKey="plan.status" />}
          value={plan.status ?? "Active"}
          onChange={(e) => set("status", e.target.value)}
          disabled={disabled}
          {...menuProps}
        >
          <MenuItem value="Active">Active</MenuItem>
          <MenuItem value="Inactive">Inactive</MenuItem>
          <MenuItem value="Out Of Stock">Out of Stock</MenuItem>
        </TextField>

        {!isContact && (
          <>
            <TextField
              select
              fullWidth
              size="small"
              label={
                <LabeledInfo
                  label="Unit price type"
                  infoKey="plan.unit_price_type"
                />
              }
              value={plan.unit_price_type ?? ""}
              onChange={(e) => set("unit_price_type", e.target.value)}
              disabled={disabled}
              error={!!err.unit_price_type}
              {...menuProps}
            >
              <MenuItem value="">--select--</MenuItem>
              <MenuItem value="Partial">Partial</MenuItem>
              <MenuItem value="Full">Full</MenuItem>
            </TextField>

            <TextField
              select
              fullWidth
              size="small"
              label={
                <LabeledInfo
                  label="Modify orders"
                  infoKey="plan.modify_orders"
                />
              }
              value={plan.modify_orders ?? ""}
              onChange={(e) => set("modify_orders", e.target.value)}
              disabled={disabled}
              error={!!err.modify_orders}
              {...menuProps}
            >
              <MenuItem value="">--select--</MenuItem>
              <MenuItem value="Prodata">Prodata</MenuItem>
              <MenuItem value="Next">Next</MenuItem>
              <MenuItem value="Current">Current</MenuItem>
            </TextField>

            <TextField
              fullWidth
              size="small"
              type="number"
              name="trial_period_days"
              label={
                <LabeledInfo
                  label="Trial period (days)"
                  infoKey="plan.trial_period_days"
                />
              }
              value={plan.trial_period_days ?? 0}
              onChange={(e) => set("trial_period_days", e.target.value)}
              onWheel={(e) => e.target.blur()}
              disabled={disabled}
            />

            <div style={styles.validityRow}>
              <span style={styles.validityLabel}>
                <LabeledInfo
                  label="Price validity"
                  infoKey="plan.price_validity"
                />
              </span>
              <div style={styles.validityInputs}>
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 1 }}
                  value={plan.n_price_validity_interval ?? ""}
                  onChange={(e) =>
                    set("n_price_validity_interval", e.target.value)
                  }
                  onWheel={(e) => e.target.blur()}
                  disabled={lifetime || validityDisabled}
                  sx={{ width: 90 }}
                />
                <TextField
                  select
                  size="small"
                  value={plan.price_validity_interval ?? "month"}
                  onChange={(e) =>
                    set("price_validity_interval", e.target.value)
                  }
                  disabled={validityDisabled}
                  sx={{ minWidth: 130 }}
                  {...menuProps}
                >
                  <MenuItem value="day">Days</MenuItem>
                  <MenuItem value="week">Weeks</MenuItem>
                  <MenuItem value="month">Months</MenuItem>
                  <MenuItem value="year">Years</MenuItem>
                  <MenuItem value="lifetime">No Expiry</MenuItem>
                </TextField>
              </div>
              {validityTooShort && (
                <div
                  style={{
                    flexBasis: "100%",
                    color: "#c53030",
                    fontSize: 12,
                    marginTop: 4,
                  }}
                >
                  Price validity cannot be shorter than the billing period
                  ({plan.n_interval || 1} {plan.interval}). A price has to hold
                  for at least one full cycle.
                </div>
              )}
            </div>
          </>
        )}

        <TextField
          fullWidth
          size="small"
          name="tags"
          label={<LabeledInfo label="Tags (comma separated)" infoKey="plan.tags" />}
          value={tagsValue}
          onChange={(e) => set("tags", e.target.value)}
          disabled={disabled}
          sx={styles.full}
        />
      </div>

      {/* Plan highlights */}
      <div style={styles.section}>
        <div style={styles.sectionHead}>
          <LabeledInfo label="Plan highlights" infoKey="plan.plan_highlight" />
        </div>
        <ul style={styles.hlList}>
          {highlights.map((h, i) => (
            <li key={i} style={styles.hlItem}>
              <span style={styles.hlCheck}>✓</span>
              <span style={{ flex: 1 }}>{h}</span>
              {isOwner && (
                <>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setHlEdit(i);
                      setHlInput(h);
                    }}
                  >
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton size="small" onClick={() => removeHighlight(i)}>
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </>
              )}
            </li>
          ))}
        </ul>
        {isOwner && (
          <div style={styles.hlAdd}>
            <TextField
              size="small"
              placeholder={hlEdit != null ? "Edit highlight" : "Add highlight"}
              value={hlInput}
              onChange={(e) => setHlInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitHighlight();
                }
              }}
              sx={{ flex: 1 }}
            />
            <button
              type="button"
              style={styles.hlBtn}
              onClick={commitHighlight}
            >
              {hlEdit != null ? "Update" : "Add"}
            </button>
          </div>
        )}
      </div>

      {/* Unit prices sub-stepper (hidden for Contact us) */}
      {!isContact && (
        <div style={styles.section}>
          <div style={styles.sectionHead}>
            <span style={styles.labelRow}>
              Unit prices
              <FieldInfo text={FIELD_INFO["unit.marketplace_price"]} />
            </span>
          </div>
          {err.price && (
            <div style={styles.errText}>
              Add at least one unit-price tier for an Active plan.
            </div>
          )}
          {children}
        </div>
      )}

      {isContact && (
        <div style={styles.contactNote}>
          Contact-us plan — no price shown. The enquiry is emailed to the
          product owner and the marketplace shows a contact card.
        </div>
      )}

      {/* Row actions */}
      {isOwner && (
        <div style={styles.actions}>
          <button
            type="button"
            style={styles.dupBtn}
            onClick={() => onDuplicate(plan.id)}
          >
            Duplicate plan
          </button>
          {allowDelete ? (
            <button
              type="button"
              style={styles.delBtn}
              onClick={() => onDelete(plan)}
            >
              Delete plan
            </button>
          ) : (
            <span style={styles.lockNote}>
              Can't delete — used in a subscription. Mark Inactive instead.
            </span>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  wrap: { display: "flex", flexDirection: "column", gap: 16 },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 14,
  },
  full: { gridColumn: "1 / -1" },
  labelRow: { display: "inline-flex", alignItems: "center", gap: 2 },
  validityRow: { gridColumn: "1 / -1", display: "flex", flexDirection: "column", gap: 6 },
  validityLabel: { fontSize: 13, color: "#374151" },
  validityInputs: { display: "flex", gap: 10 },
  section: {
    border: "1px solid #e6e6ee",
    borderRadius: 12,
    padding: 14,
    background: "#fff",
  },
  sectionHead: { fontWeight: 700, fontSize: 14, marginBottom: 8, color: "#111827" },
  hlList: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 },
  hlItem: { display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "#374151" },
  hlCheck: { color: "#10b981", fontWeight: 800 },
  hlAdd: { display: "flex", gap: 8, marginTop: 10 },
  hlBtn: {
    border: "none",
    background: "#4f46e5",
    color: "#fff",
    borderRadius: 8,
    padding: "0 16px",
    fontWeight: 600,
    cursor: "pointer",
  },
  errText: { color: "#dc2626", fontSize: 13, marginBottom: 8 },
  contactNote: {
    border: "1px dashed #d4d7e0",
    borderRadius: 12,
    padding: 14,
    fontSize: 13,
    color: "#6b7280",
  },
  actions: { display: "flex", alignItems: "center", gap: 12 },
  dupBtn: {
    border: "1px solid #4f46e5",
    background: "#eef0ff",
    color: "#3730a3",
    borderRadius: 8,
    padding: "8px 16px",
    fontWeight: 600,
    cursor: "pointer",
  },
  delBtn: {
    border: "1px solid #ef4444",
    background: "#fff",
    color: "#ef4444",
    borderRadius: 8,
    padding: "8px 16px",
    fontWeight: 600,
    cursor: "pointer",
  },
  lockNote: { fontSize: 12, color: "#9ca3af" },
};
